import React, { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, CircularProgress, Collapse, Typography } from '@mui/material';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRounded from '@mui/icons-material/RadioButtonUncheckedRounded';

export interface ChatStep {
  text: string;
  /** A factual one-line result, e.g. "1,248 rows × 6 columns · Snowflake · 3.2s". */
  receipt?: string;
}

export interface ChatWorkStepsProps {
  steps: ChatStep[];
  /**
   * `live` — the turn is running: an open list and a timer.
   * `waiting` — stopped on something only the analyst can answer, e.g. a
   * query to approve: the steps stay open and nothing ticks along.
   * `done` / `stopped` — folded into one line that opens the list.
   */
  state: 'live' | 'waiting' | 'done' | 'stopped';
  /** How long the turn took, for `done` and `stopped`. */
  seconds?: number;
  /** When the turn started, ms since epoch, for the live timer. */
  startedAt?: number | null;
  /** What is happening right now, e.g. "Fetching data…". Defaults to "Working". */
  action?: string;
  /** A second line while live, e.g. "Still working — large queries take a while". */
  note?: string;
  /** How to show a step's text; plain text by default. */
  renderStep?: (text: string) => React.ReactNode;
}

function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

/** Whole seconds since `startedAt`, ticking once a second while set. */
function useElapsed(startedAt: number | null | undefined): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  return startedAt ? Math.max(0, Math.floor((now - startedAt) / 1000)) : null;
}

/**
 * The work behind an answer, shown while it happens and folded once it is done.
 *
 * Live, it is an open list — newest step last, an open circle on the one
 * running, a tick on each one done — under a line saying what is happening
 * and for how long. Finished, it becomes "Worked for 14s · 4 steps", which
 * opens the same list.
 *
 * Two things move while it works, and they say different things: a soft
 * shimmer across what is happening, and a turning ring on the one step still
 * running. The ring has to be an arc — a plain circle spun on its own centre
 * looks perfectly still. Waiting is not working, so the ring stops and the
 * step falls back to an open circle. With reduced motion, nothing moves.
 */
export function ChatWorkSteps({
  steps,
  state,
  seconds,
  startedAt,
  action,
  note,
  renderStep,
}: ChatWorkStepsProps) {
  const live = state === 'live';
  // Waiting is not working: no timer, nothing moving, and the steps stay in
  // view so it is clear where the turn stopped and why.
  const waiting = state === 'waiting';
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (waiting) setOpen(true);
  }, [waiting]);
  const elapsed = useElapsed(live ? startedAt : null);
  const listRef = useRef<HTMLOListElement | null>(null);
  const n = steps.length;

  // Keep the newest step in view while the list grows.
  useEffect(() => {
    if (live && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [live, n]);

  if (!live && n === 0) return null;
  const count = `${n} step${n === 1 ? '' : 's'}`;

  const list = n > 0 && (
    <Box
      component="ol"
      ref={listRef}
      sx={(t) => ({
        listStyle: 'none',
        m: 0,
        mt: 0.75,
        pl: 1,
        borderLeft: `2px solid ${t.tokens.border.subtle}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        maxHeight: live ? 168 : undefined,
        overflowY: live ? 'auto' : undefined,
      })}
    >
      {steps.map((step, i) => {
        const running = (live || waiting) && i === n - 1 && !step.receipt;
        // Waiting is stopped on the analyst, not working, so nothing turns.
        const spinning = running && live;
        return (
          <Box component="li" key={i} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
            <Box
              aria-hidden
              sx={(t) => ({
                ...t.typography.caption,
                display: 'flex',
                alignItems: 'center',
                height: '1.5em',
                flexShrink: 0,
                color: t.tokens.text.tertiary,
              })}
            >
              {spinning ? (
                <CircularProgress
                  size="1em"
                  thickness={5}
                  sx={(t) => ({
                    color: t.tokens.text.secondary,
                    // Held still rather than hidden, so the step it is on is
                    // still the one that reads differently from the ticks.
                    '@media (prefers-reduced-motion: reduce)': {
                      animation: 'none',
                      '& .MuiCircularProgress-circle': { animation: 'none' },
                    },
                  })}
                />
              ) : running ? (
                <RadioButtonUncheckedRounded fontSize="inherit" />
              ) : (
                <CheckCircleRounded fontSize="inherit" />
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              {renderStep ? (
                renderStep(step.text)
              ) : (
                <Typography variant="caption" component="div" sx={(t) => ({ color: t.tokens.text.secondary })}>
                  {step.text}
                </Typography>
              )}
              {step.receipt && (
                <Typography
                  variant="caption"
                  component="div"
                  sx={(t) => ({
                    fontFamily: t.typography.fontFamilyMono,
                    fontVariantNumeric: 'tabular-nums',
                    color: t.tokens.text.tertiary,
                  })}
                >
                  {step.receipt}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  if (live) {
    const doing = action?.trim() || (n === 0 ? 'Thinking…' : 'Working');
    const facts = [n > 0 ? count : '', elapsed ? formatDuration(elapsed) : ''].filter(Boolean);
    return (
      <Box aria-busy="true">
        <Typography
          variant="caption"
          component="div"
          sx={(t) => ({ color: t.tokens.text.secondary, fontVariantNumeric: 'tabular-nums' })}
        >
          <Box
            component="span"
            sx={(t) => ({
              '@keyframes kit-steps-shimmer': {
                from: { backgroundPosition: '150% 0' },
                to: { backgroundPosition: '-50% 0' },
              },
              '@media (prefers-reduced-motion: no-preference)': {
                // The words themselves carry a lighter band that sweeps across.
                backgroundImage: `linear-gradient(90deg, ${t.tokens.text.secondary} 40%, ${t.tokens.text.primary} 50%, ${t.tokens.text.secondary} 60%)`,
                backgroundSize: '200% 100%',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
                animation: 'kit-steps-shimmer 2s linear infinite',
              },
            })}
          >
            {doing}
          </Box>
          {facts.map((f) => ` · ${f}`).join('')}
        </Typography>
        {note && (
          <Typography variant="caption" component="div" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            {note}
          </Typography>
        )}
        {list}
      </Box>
    );
  }

  const took = seconds ? formatDuration(seconds) : '';
  const summary = state === 'stopped'
    ? `Stopped${took ? ` after ${took}` : ''} · ${count}`
    : waiting
      ? `Waiting for your approval · ${count}`
      : `Worked${took ? ` for ${took}` : ''} · ${count}`;

  return (
    <Box>
      <ButtonBase
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        sx={(t) => ({
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          py: 0.25,
          pr: 0.5,
          borderRadius: `${t.ds.radius.xs}px`,
          color: t.tokens.text.secondary,
          '&:hover': { color: t.tokens.text.primary },
          '&.Mui-focusVisible': { boxShadow: t.tokens.shadow.focusRing },
          '& svg': {
            transition: `transform ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
            transform: open ? 'rotate(90deg)' : 'none',
          },
        })}
      >
        <ChevronRightRounded fontSize="small" />
        <Typography variant="caption">{summary}</Typography>
      </ButtonBase>
      <Collapse in={open} unmountOnExit>
        {list}
      </Collapse>
    </Box>
  );
}
